import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@repo/ui/lib/utils";
import Icon from "@repo/ui/components/ui/icon/icon";
import { Dispatch, forwardRef, SetStateAction } from "react";

const DialogWrapper = DialogPrimitive.Root;

// const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

// const DialogClose = DialogPrimitive.Close

const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "absolute inset-0 z-50 backdrop-blur-[20px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content> & { preventClose?: boolean },
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    preventClose?: boolean;
  }
>(({ className, children, preventClose, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      onInteractOutside={(e) => {
        if (preventClose) {
          e.preventDefault();
        }
      }}
      ref={ref}
      className={cn(
        " text-black light overflow-hidden flex flex-col w-[670px] max-w-[670px] max-h-[80%] max-md:w-[90%] fixed left-[50%] top-[50%] rounded-[20px] z-50 translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-200 bg-primary-white py-[40px] shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className,
      )}
      {...props}
    >
      <div className="text-primary-black overflow-y-auto flex-1 px-[40px] max-md:px-[20px]">
        {children}
      </div>
      {!preventClose && (
        <DialogPrimitive.Close className="fixed right-[40px] top-[40px] max-md:right-[20px]">
          <Icon
            className="h-[40px] w-[40px] max-md:h-[30px] max-md:w-[30px] fill-secondary-black"
            icon="Close"
          />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "mb-[30px] max-md:mb-[20px] pr-[40px] text-primary-black ",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 text-primary-black ",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-[40px] tracking-wide text-primary-black font-bold leading-none max-md:w-full max-md:text-[24px]",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      "max-md:text-sm text-base font-bold mt-5 text-primary-black",
      className,
    )}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

interface DialogProps<T> {
  header?: string;
  description?: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<T>>;
  children: React.ReactNode;
  contentClassName?: string;
  preventClose?: boolean;
  error?: boolean;
}

const Dialog = ({
  header,
  description,
  open,
  setOpen,
  children,
  preventClose,
  contentClassName,
  error,
}: DialogProps<boolean>) => {
  return (
    <DialogWrapper open={open} onOpenChange={setOpen}>
      <DialogContent
        preventClose={preventClose}
        className={cn(contentClassName)}
      >
        <DialogHeader>
          <DialogTitle className={cn({ "text-red-500": error })}>
            {header}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </DialogWrapper>
  );
};

export {
  DialogWrapper,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogPortal,
  DialogOverlay,
};
export default Dialog;
